import camelCase from 'lodash/camelCase';
import has from 'lodash/has';
import startsWith from 'lodash/startsWith';

import {
    isFileTypeArchive,
    isFileTypeAudio,
    isFileTypeCsv,
    isFileTypeExcel,
    isFileTypeImage,
    isFileTypePdf,
    isFileTypeVideo,
    isFileTypeWord
} from '@utils/file';

import icons from '@plugins/vuetify/icons';

/**
 * Return the icon for the given string
 * @param {String} key
 * @returns {String}
 */
export const getIcon = (key = 'default', prefix = null) => {
    if (
        startsWith(key, 'fas ') ||
        startsWith(key, 'far ') ||
        startsWith(key, 'fal ') ||
        startsWith(key, 'fat ') ||
        startsWith(key, 'fad ') ||
        startsWith(key, 'fab ')
    ) {
        return key;
    }

    key = camelCase(key);

    if (!has(icons, key)) {
        return 'fal fa-question-circle';
    }

    let icon = icons[`${key}`];

    if (prefix) {
        icon = icon.replace('fas ', '');
        icon = icon.replace('far ', '');
        icon = icon.replace('fal ', '');
        icon = icon.replace('fat ', '');
        icon = icon.replace('fad ', '');
        icon = icon.replace('fab ', '');

        return `${prefix} ${icon}`;
    }

    return icon;
};

export const getFileTypeIcon = type => {
    if (isFileTypeArchive(type)) {
        return 'fileArchive';
    } else if (isFileTypeAudio(type)) {
        return 'fileAudio';
    } else if (isFileTypeCsv(type)) {
        return 'fileCsv';
    } else if (isFileTypeExcel(type)) {
        return 'fileExcel';
    } else if (isFileTypeImage(type)) {
        return 'fileImage';
    } else if (isFileTypePdf(type)) {
        return 'filePdf';
    } else if (isFileTypeVideo(type)) {
        return 'fileVideo';
    } else if (isFileTypeWord(type)) {
        return 'fileWord';
    }

    return 'file';
};

export const getImage = (src, width = null, height = null) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onerror = () => reject();

        image.onload = () => {
            const format = getImageFormat(image);

            if (image.width === 0 || image.height === 0) {
                reject(new Error('Failed to load image: ' + src));
            }

            const response = {
                width: image.width,
                height: image.height,
                type: 'png',
                src: null
            };

            if (width && height === null) {
                response.width = width;
                response.height = (image.height / image.width) * response.width;
            } else if (height && width === null) {
                response.height = height;
                response.width = (image.width / image.height) * response.height;
            } else if (width && height) {
                response.width = width;
                response.height = height;
            }

            response.type = format.replace('image/', '');
            response.src = getImageDataURI(image, format);

            resolve(response);
        };

        image.src = src;
    });
};

export const getImageFormat = image => {
    const src = image.src;
    const match = src.match(/\.(jpeg|jpg|png|gif|webp|bmp|svg)$/i);
    if (match) {
        return `image/${match[1]}`.replace('jpg', 'jpeg');
    }
    return 'image/png';
};

/**
 * Return an object to preview an image File
 * @param {File} file
 */
export const toImage = file => {
    try {
        return {
            name: file.name,
            size: file.size,
            type: file.type,
            src: URL.createObjectURL(file)
        };
    } catch (error) {
        return null;
    }
};

export const getImageDataURI = (image, format = null) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = image.width;
    canvas.height = image.height;

    ctx.drawImage(image, 0, 0);

    const imageFormat = format || getImageFormat(image);

    const dataURI = canvas.toDataURL(imageFormat);

    return dataURI;
};
